import { useState, useEffect } from 'react';
import { ModuleHeader } from '@/components/layout/ModuleHeader';
import { Sliders, Plus, Edit, Trash2, Power, Save, X, Loader2, Euro, FileText, Package, Layers, Pill, Box, Ruler, Mail, Users, CreditCard, Star } from 'lucide-react';
import { useAlert } from '@/components/AlertProvider';
import api, { apiService } from '@/services/api';
import gammeConfigService, { PaymentTerm } from '@/services/gammeConfigService';
import BrandPricingModal from '@/components/BrandPricingModal';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

interface ConfigItem {
  id: number;
  name: string;
  is_active: boolean;
}

interface DocCategory {
  value: string;
  label: string;
}

type ConfigType = 'brands' | 'ranges' | 'forms' | 'packagings' | 'units';

const DocCategorySection = ({
  onRefresh
}: {
  onRefresh: () => void;
}) => {
  const { showSuccess, showError } = useAlert();
  const [categories, setCategories] = useState<DocCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; value: string | null }>({ open: false, value: null });
  const sortedCategories: DocCategory[] = [...categories].sort((first: DocCategory, second: DocCategory) => (
    first.label.localeCompare(second.label, 'fr', { sensitivity: 'base' })
  ));

  const loadCategories = async () => {
    try {
      setLoading(true);
      const settings = await apiService.getGroupSettings();
      setCategories(settings.document_categories || []);
    } catch (error) {
      console.error('Failed to load categories', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleSave = async (newCategories: DocCategory[], renames?: Record<string, string>) => {
    setSubmitting(true);
    try {
      await apiService.updateGroupSettings({
        document_categories: newCategories,
        category_renames: renames
      });
      setCategories(newCategories);
      showSuccess('Catégories mises à jour');
      onRefresh();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const value = newLabel.trim();
    if (categories.find(c => c.value.toLowerCase() === value.toLowerCase())) {
      showError('Cette catégorie existe déjà');
      return;
    }
    const updated = [...categories, { value, label: newLabel.trim() }];
    handleSave(updated);
    setNewLabel('');
    setIsAdding(false);
  };

  const handleUpdate = (oldValue: string) => {
    if (!editLabel.trim()) return;
    const targetIndex = categories.findIndex((category) => category.value === oldValue);
    if (targetIndex === -1) return;

    const newLabel = editLabel.trim();
    const newValue = newLabel; // Use label as value directly

    const updated = [...categories];
    updated[targetIndex] = { value: newValue, label: newLabel };

    // Track renames for document migration
    const renames: Record<string, string> = {};
    if (oldValue !== newValue) {
      renames[oldValue] = newValue;
    }

    handleSave(updated, renames);
    setEditingId(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteDialog.value) return;
    const updated = categories.filter((category) => category.value !== deleteDialog.value);
    handleSave(updated);
    setDeleteDialog({ open: false, value: null });
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-purple-200 dark:border-purple-800/50 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md hover:border-purple-300">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-purple-50/50 dark:bg-purple-900/10">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
            <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100">Catégories de docs</h3>
          <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">{categories.length}</span>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="p-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          title="Ajouter"
          aria-label="Ajouter"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {isAdding && (
          <div className="flex items-center gap-1.5 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
            <input
              autoFocus
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Nouvelle catégorie..."
              aria-label="Nouvelle catégorie"
              className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-1 focus:ring-purple-500"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button onClick={handleAdd} disabled={submitting} className="p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50" title="Enregistrer" aria-label="Enregistrer">
              <Save className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setIsAdding(false)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-8 text-slate-500 italic">Aucune catégorie</div>
        ) : (
          sortedCategories.map((item) => (
            <div key={item.value} className="group flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-lg hover:bg-white dark:hover:bg-slate-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all">
              {editingId === item.value ? (
                <div className="flex items-center gap-1.5 flex-1 mr-2">
                  <input
                    autoFocus
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    aria-label="Modifier la catégorie"
                    className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-purple-500"
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(item.value)}
                  />
                  <button onClick={() => handleUpdate(item.value)} disabled={submitting} className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50" title="Enregistrer" aria-label="Enregistrer">
                    <Save className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        setEditingId(item.value);
                        setEditLabel(item.label);
                      }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                      title="Modifier"
                      aria-label="Modifier"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteDialog({ open: true, value: item.value })}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      title="Supprimer"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, value: open ? deleteDialog.value : null })}
        title="Supprimer la catégorie"
        description="Êtes-vous sûr de vouloir supprimer cette catégorie ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={submitting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};


const ClientNotificationRecipientsSection = ({
  onRefresh
}: {
  onRefresh: () => void;
}) => {
  const { showSuccess, showError } = useAlert();
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newType, setNewType] = useState<'cc' | 'bcc'>('cc');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState<{ type: 'cc' | 'bcc'; email: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const settings = await apiService.getGroupSettings();
      setCcEmails(Array.isArray(settings.client_notification_cc) ? settings.client_notification_cc : []);
      setBccEmails(Array.isArray(settings.client_notification_bcc) ? settings.client_notification_bcc : []);
    } catch (error) {
      console.error('Failed to load client notification recipients', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const normalizeEmail = (value: string) => value.trim();
  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const existsInAny = (email: string) => {
    const key = email.toLowerCase();
    return ccEmails.some((e) => e.toLowerCase() === key) || bccEmails.some((e) => e.toLowerCase() === key);
  };

  const save = async (nextCc: string[], nextBcc: string[]) => {
    setSubmitting(true);
    try {
      await apiService.updateGroupSettings({
        client_notification_cc: nextCc,
        client_notification_bcc: nextBcc
      });
      setCcEmails(nextCc);
      setBccEmails(nextBcc);
      showSuccess('Emails CC/CCI mis à jour');
      onRefresh();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = async () => {
    const email = normalizeEmail(newEmail);
    if (!email) return;
    if (!isValidEmail(email)) {
      showError('Email invalide');
      return;
    }
    if (existsInAny(email)) {
      showError('Cet email existe déjà');
      return;
    }

    const nextCc = newType === 'cc' ? [...ccEmails, email] : ccEmails;
    const nextBcc = newType === 'bcc' ? [...bccEmails, email] : bccEmails;
    await save(nextCc, nextBcc);
    setNewEmail('');
    setIsAdding(false);
    setNewType('cc');
  };

  const handleDelete = async (type: 'cc' | 'bcc', email: string) => {
    const nextCc = type === 'cc' ? ccEmails.filter((e) => e !== email) : ccEmails;
    const nextBcc = type === 'bcc' ? bccEmails.filter((e) => e !== email) : bccEmails;
    await save(nextCc, nextBcc);
  };

  const handleStartEdit = (type: 'cc' | 'bcc', email: string) => {
    setEditing({ type, email });
    setEditValue(email);
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setEditValue('');
  };

  const handleConfirmEdit = async () => {
    if (!editing) return;
    const nextEmail = normalizeEmail(editValue);
    if (!nextEmail) return;
    if (!isValidEmail(nextEmail)) {
      showError('Email invalide');
      return;
    }
    const key = nextEmail.toLowerCase();
    const oldKey = editing.email.toLowerCase();
    if (key !== oldKey && existsInAny(nextEmail)) {
      showError('Cet email existe déjà');
      return;
    }

    const nextCc = editing.type === 'cc'
      ? ccEmails.map((e) => (e === editing.email ? nextEmail : e))
      : ccEmails;
    const nextBcc = editing.type === 'bcc'
      ? bccEmails.map((e) => (e === editing.email ? nextEmail : e))
      : bccEmails;

    await save(nextCc, nextBcc);
    setEditing(null);
    setEditValue('');
  };

  const sortedCc = [...ccEmails].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  const sortedBcc = [...bccEmails].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-purple-200 dark:border-purple-800/50 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md hover:border-purple-300">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-purple-50/50 dark:bg-purple-900/10">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
            <Mail className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100">Notifications clients</h3>
          <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">{ccEmails.length + bccEmails.length}</span>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="p-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          title="Ajouter"
          aria-label="Ajouter"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {isAdding && (
          <div className="flex items-center gap-1.5 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
            <select
              value={newType}
              onChange={(e) => setNewType((e.target.value as 'cc' | 'bcc') || 'cc')}
              className="px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-1 focus:ring-purple-500"
              aria-label="Type"
              title="Type"
            >
              <option value="cc">CC</option>
              <option value="bcc">CCI</option>
            </select>
            <input
              autoFocus
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="email@domaine.com"
              aria-label="Ajouter un email"
              title="Ajouter un email"
              className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-1 focus:ring-purple-500"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button onClick={handleAdd} disabled={submitting} className="p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50" title="Enregistrer" aria-label="Enregistrer">
              <Save className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setIsAdding(false)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 px-1">CC (visible)</div>
              {sortedCc.length === 0 ? (
                <div className="text-center py-3 text-slate-500 italic">Aucun email CC</div>
              ) : (
                sortedCc.map((email) => (
                  <div key={`cc:${email}`} className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-lg hover:bg-white dark:hover:bg-slate-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all">
                    {editing?.type === 'cc' && editing.email === email ? (
                      <div className="flex items-center gap-1.5 flex-1 mr-2">
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          aria-label="Modifier l'email"
                          title="Modifier l'email"
                          className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-purple-500"
                          onKeyDown={(e) => e.key === 'Enter' && handleConfirmEdit()}
                        />
                        <button
                          onClick={handleConfirmEdit}
                          disabled={submitting}
                          className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          title="Enregistrer"
                          aria-label="Enregistrer"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={submitting}
                          className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-50"
                          title="Annuler"
                          aria-label="Annuler"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{email}</span>
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => handleStartEdit('cc', email)}
                            disabled={submitting}
                            className="p-1.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded disabled:opacity-50"
                            title="Modifier"
                            aria-label="Modifier"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete('cc', email)}
                            disabled={submitting}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                            title="Supprimer"
                            aria-label="Supprimer"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 px-1">CCI (caché)</div>
              {sortedBcc.length === 0 ? (
                <div className="text-center py-3 text-slate-500 italic">Aucun email CCI</div>
              ) : (
                sortedBcc.map((email) => (
                  <div key={`bcc:${email}`} className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-lg hover:bg-white dark:hover:bg-slate-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all">
                    {editing?.type === 'bcc' && editing.email === email ? (
                      <div className="flex items-center gap-1.5 flex-1 mr-2">
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          aria-label="Modifier l'email"
                          title="Modifier l'email"
                          className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-purple-500"
                          onKeyDown={(e) => e.key === 'Enter' && handleConfirmEdit()}
                        />
                        <button
                          onClick={handleConfirmEdit}
                          disabled={submitting}
                          className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          title="Enregistrer"
                          aria-label="Enregistrer"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={submitting}
                          className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-50"
                          title="Annuler"
                          aria-label="Annuler"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{email}</span>
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => handleStartEdit('bcc', email)}
                            disabled={submitting}
                            className="p-1.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded disabled:opacity-50"
                            title="Modifier"
                            aria-label="Modifier"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete('bcc', email)}
                            disabled={submitting}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
                            title="Supprimer"
                            aria-label="Supprimer"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface ContactRole {
  value: string;
  label: string;
}

const ContactRolesSection = ({
  onRefresh
}: {
  onRefresh: () => void;
}) => {
  const { showSuccess, showError } = useAlert();
  const [roles, setRoles] = useState<ContactRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; value: string | null }>({ open: false, value: null });
  const sortedRoles: ContactRole[] = [...roles].sort((first: ContactRole, second: ContactRole) => (
    first.label.localeCompare(second.label, 'fr', { sensitivity: 'base' })
  ));

  const loadRoles = async () => {
    try {
      setLoading(true);
      const settings = await apiService.getGroupSettings();
      setRoles(settings.contact_roles || []);
    } catch (error) {
      console.error('Failed to load contact roles', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const handleSave = async (newRoles: ContactRole[], renames?: Record<string, string>) => {
    setSubmitting(true);
    try {
      await apiService.updateGroupSettings({
        contact_roles: newRoles,
        contact_role_renames: renames
      });
      setRoles(newRoles);
      showSuccess('Rôles mis à jour');
      onRefresh();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const value = newLabel.trim().toLowerCase().replace(/\s+/g, '_');
    if (roles.find(r => r.value.toLowerCase() === value.toLowerCase())) {
      showError('Ce rôle existe déjà');
      return;
    }
    const updated = [...roles, { value, label: newLabel.trim() }];
    handleSave(updated);
    setNewLabel('');
    setIsAdding(false);
  };

  const handleUpdate = (oldValue: string) => {
    if (!editLabel.trim()) return;
    const targetIndex = roles.findIndex((role) => role.value === oldValue);
    if (targetIndex === -1) return;

    const newLabelTrimmed = editLabel.trim();
    const newValue = newLabelTrimmed.toLowerCase().replace(/\s+/g, '_');

    const updated = [...roles];
    updated[targetIndex] = { value: newValue, label: newLabelTrimmed };

    const renames: Record<string, string> = {};
    if (oldValue !== newValue) {
      renames[oldValue] = newValue;
    }

    handleSave(updated, renames);
    setEditingId(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteDialog.value) return;
    const updated = roles.filter((role) => role.value !== deleteDialog.value);
    handleSave(updated);
    setDeleteDialog({ open: false, value: null });
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-indigo-200 dark:border-indigo-800/50 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md hover:border-indigo-300">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-indigo-50/50 dark:bg-indigo-900/10">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg">
            <Users className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100">Rôles contacts</h3>
          <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">{roles.length}</span>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          title="Ajouter"
          aria-label="Ajouter"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {isAdding && (
          <div className="flex items-center gap-1.5 p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-700">
            <input
              autoFocus
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Nouveau rôle..."
              className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-1 focus:ring-indigo-500"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button onClick={handleAdd} disabled={submitting} className="p-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
              <Save className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setIsAdding(false)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : roles.length === 0 ? (
          <div className="text-center py-8 text-slate-500 italic">Aucun rôle</div>
        ) : (
          sortedRoles.map((item) => (
            <div key={item.value} className="group flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-lg hover:bg-white dark:hover:bg-slate-800 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all">
              {editingId === item.value ? (
                <div className="flex items-center gap-1.5 flex-1 mr-2">
                  <input
                    autoFocus
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-indigo-500"
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(item.value)}
                  />
                  <button onClick={() => handleUpdate(item.value)} disabled={submitting} className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                    <Save className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-indigo-500"></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        setEditingId(item.value);
                        setEditLabel(item.label);
                      }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                      title="Modifier"
                      aria-label="Modifier"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteDialog({ open: true, value: item.value })}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      title="Supprimer"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, value: open ? deleteDialog.value : null })}
        title="Supprimer le rôle"
        description="Êtes-vous sûr de vouloir supprimer ce rôle ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={submitting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

const PaymentTermsSection = () => {
  const { showSuccess, showError } = useAlert();
  const [terms, setTerms] = useState<PaymentTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const loadTerms = async () => {
    try {
      setLoading(true);
      const data = await gammeConfigService.listAllPaymentTerms();
      setTerms(data);
    } catch (error) {
      console.error('Failed to load payment terms', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTerms();
  }, []);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSubmitting(true);
    try {
      await gammeConfigService.createPaymentTerm({ label: newLabel.trim() });
      showSuccess('Terme ajouté avec succès');
      setNewLabel('');
      setIsAdding(false);
      loadTerms();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editLabel.trim()) return;
    setSubmitting(true);
    try {
      await gammeConfigService.updatePaymentTerm(id, { label: editLabel.trim(), value: editLabel.trim() });
      showSuccess('Mis à jour avec succès');
      setEditingId(null);
      loadTerms();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleDefault = async (term: PaymentTerm) => {
    setSubmitting(true);
    try {
      await gammeConfigService.updatePaymentTerm(term.id, { is_default: !term.is_default });
      loadTerms();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (term: PaymentTerm) => {
    setSubmitting(true);
    try {
      await gammeConfigService.updatePaymentTerm(term.id, { is_active: !term.is_active });
      loadTerms();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.id) return;
    setIsDeleting(true);
    try {
      await gammeConfigService.deletePaymentTerm(deleteDialog.id);
      showSuccess('Supprimé avec succès');
      setDeleteDialog({ open: false, id: null });
      loadTerms();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSeed = async () => {
    setSubmitting(true);
    try {
      await gammeConfigService.seedPaymentTerms();
      showSuccess('Termes par défaut initialisés');
      loadTerms();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const activeCount = terms.filter(t => t.is_active).length;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-teal-200 dark:border-teal-800/50 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md hover:border-teal-300">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-teal-50/50 dark:bg-teal-900/10">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-teal-100 dark:bg-teal-900/40 rounded-lg">
            <CreditCard className="h-4 w-4 text-teal-600 dark:text-teal-400" />
          </div>
          <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100">Termes de règlement</h3>
          <span className="px-1.5 py-0.5 text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded">
            {activeCount}/{terms.length}
          </span>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="p-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          title="Ajouter"
          aria-label="Ajouter"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {isAdding && (
          <div className="flex items-center gap-1.5 p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-700">
            <input
              autoFocus
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Nouveau terme..."
              aria-label="Nouveau terme de règlement"
              className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-1 focus:ring-teal-500"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button onClick={handleAdd} disabled={submitting} className="p-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50" title="Enregistrer" aria-label="Enregistrer">
              <Save className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setIsAdding(false)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : terms.length === 0 ? (
          <div className="text-center py-6">
            <div className="text-slate-500 italic mb-2">Aucun terme</div>
            <button
              onClick={handleSeed}
              disabled={submitting}
              className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 disabled:opacity-50"
            >
              Initialiser les termes par défaut
            </button>
          </div>
        ) : (
          terms.map((term) => (
            <div key={term.id} className={`group flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-lg hover:bg-white dark:hover:bg-slate-800 hover:border-teal-300 dark:hover:border-teal-600 transition-all ${!term.is_active ? 'opacity-50' : ''}`}>
              {editingId === term.id ? (
                <div className="flex items-center gap-1.5 flex-1 mr-2">
                  <input
                    autoFocus
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    aria-label="Modifier le terme"
                    className="flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-teal-500"
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(term.id)}
                  />
                  <button onClick={() => handleUpdate(term.id)} disabled={submitting} className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50" title="Enregistrer" aria-label="Enregistrer">
                    <Save className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${term.is_active ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{term.label}</span>
                    {term.is_default && (
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => handleToggleDefault(term)}
                      className={`p-1.5 rounded ${term.is_default ? 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      title={term.is_default ? 'Retirer par défaut' : 'Définir par défaut'}
                      aria-label={term.is_default ? 'Retirer par défaut' : 'Définir par défaut'}
                    >
                      <Star className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(term)}
                      className={`p-1.5 rounded ${term.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      title={term.is_active ? 'Désactiver' : 'Activer'}
                      aria-label={term.is_active ? 'Désactiver' : 'Activer'}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(term.id);
                        setEditLabel(term.label);
                      }}
                      className="p-1.5 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded"
                      title="Modifier"
                      aria-label="Modifier"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteDialog({ open: true, id: term.id })}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      title="Supprimer"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: open ? deleteDialog.id : null })}
        title="Supprimer le terme de règlement"
        description="Êtes-vous sûr de vouloir supprimer ce terme ? Les clients utilisant ce terme ne seront pas affectés."
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

const getSectionIcon = (type: ConfigType) => {
  const iconMap = {
    brands: Package,
    ranges: Layers,
    forms: Pill,
    packagings: Box,
    units: Ruler
  };
  return iconMap[type];
};

const getSectionColor = (type: ConfigType) => {
  const colorMap = {
    brands: 'blue',
    ranges: 'emerald',
    forms: 'amber',
    packagings: 'rose',
    units: 'cyan'
  };
  return colorMap[type];
};

const ConfigSection = ({
  title,
  type,
  items,
  loading,
  onRefresh,
  onPricingClick
}: {
  title: string;
  type: ConfigType;
  items: ConfigItem[];
  loading: boolean;
  onRefresh: () => void;
  onPricingClick?: (itemName: string) => void;
}) => {
  const Icon = getSectionIcon(type);
  const color = getSectionColor(type);
  const activeCount = items.filter(item => item.is_active).length;
  const sortedItems: ConfigItem[] = [...items].sort((first: ConfigItem, second: ConfigItem) => (
    first.name.localeCompare(second.name, 'fr', { sensitivity: 'base' })
  ));
  const { showSuccess, showError } = useAlert();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editName, setEditName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleAdd = async () => {
    if (!newItemName.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/product-config/${type}`, { name: newItemName, is_active: true });
      showSuccess('Élément ajouté avec succès');
      setNewItemName('');
      setIsAdding(false);
      onRefresh();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: number, name: string, is_active: boolean) => {
    setSubmitting(true);
    try {
      await api.patch(`/product-config/${type}/${id}`, { name, is_active });
      showSuccess('Mis à jour avec succès');
      setEditingId(null);
      onRefresh();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: number) => {
    setDeleteDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.id) return;

    setIsDeleting(true);
    try {
      await api.delete(`/product-config/${type}/${deleteDialog.id}`);
      showSuccess('Supprimé avec succès');
      setDeleteDialog({ open: false, id: null });
      onRefresh();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border border-${color}-200 dark:border-${color}-800/50 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-md hover:border-${color}-300`}>
      <div className={`px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-${color}-50/50 dark:bg-${color}-900/10`}>
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 bg-${color}-100 dark:bg-${color}-900/40 rounded-lg`}>
            <Icon className={`h-4 w-4 text-${color}-600 dark:text-${color}-400`} />
          </div>
          <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100">{title}</h3>
          <span className={`px-1.5 py-0.5 text-xs font-medium bg-${color}-100 dark:bg-${color}-900/30 text-${color}-700 dark:text-${color}-300 rounded`}>
            {activeCount}/{items.length}
          </span>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className={`p-1.5 bg-${color}-600 text-white rounded-lg hover:bg-${color}-700`}
          title="Ajouter"
          aria-label="Ajouter"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {isAdding && (
          <div className={`flex items-center gap-1.5 p-2 bg-${color}-50 dark:bg-${color}-900/20 rounded-lg border border-${color}-200 dark:border-${color}-700`}>
            <input
              autoFocus
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder="Nouveau..."
              aria-label={`Ajouter un élément - ${title}`}
              className={`flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:ring-1 focus:ring-${color}-500`}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <button onClick={handleAdd} disabled={submitting} className={`p-1.5 bg-${color}-600 text-white rounded hover:bg-${color}-700 disabled:opacity-50`} title="Enregistrer" aria-label="Enregistrer">
              <Save className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setIsAdding(false)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-slate-500 italic">Aucun élément</div>
        ) : (
          sortedItems.map((item) => (
            <div key={item.id} className={`group flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-lg hover:bg-white dark:hover:bg-slate-800 hover:border-${color}-300 dark:hover:border-${color}-600 transition-all`}>
              {editingId === item.id ? (
                <div className="flex items-center gap-1.5 flex-1 mr-2">
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    aria-label={`Modifier l'élément - ${title}`}
                    className={`flex-1 px-2.5 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:ring-1 focus:ring-${color}-500`}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(item.id, editName, item.is_active)}
                  />
                  <button onClick={() => handleUpdate(item.id, editName, item.is_active)} disabled={submitting} className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50" title="Enregistrer" aria-label="Enregistrer">
                    <Save className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 rounded" title="Annuler" aria-label="Annuler">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${item.is_active ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {type === 'brands' && onPricingClick && (
                      <button
                        onClick={() => onPricingClick(item.name)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded"
                        title="Configurer les paliers"
                      >
                        <Euro className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleUpdate(item.id, item.name, !item.is_active)}
                      className={`p-1.5 rounded ${item.is_active ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      title={item.is_active ? 'Désactiver' : 'Activer'}
                      aria-label={item.is_active ? 'Désactiver' : 'Activer'}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(item.id);
                        setEditName(item.name);
                      }}
                      className={`p-1.5 text-${color}-600 hover:bg-${color}-50 dark:hover:bg-${color}-900/30 rounded`}
                      title="Modifier"
                      aria-label="Modifier"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      title="Supprimer"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: open ? deleteDialog.id : null })}
        title="Supprimer l'élément"
        description="Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default function ProductConfig() {
  const [brands, setBrands] = useState<ConfigItem[]>([]);
  const [ranges, setRanges] = useState<ConfigItem[]>([]);
  const [forms, setForms] = useState<ConfigItem[]>([]);
  const [packagings, setPackagings] = useState<ConfigItem[]>([]);
  const [units, setUnits] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [brandsRes, rangesRes, formsRes, packagingsRes, unitsRes] = await Promise.all([
        api.get('/product-config/brands'),
        api.get('/product-config/ranges'),
        api.get('/product-config/forms'),
        api.get('/product-config/packagings'),
        api.get('/product-config/units')
      ]);
      setBrands(brandsRes.data);
      setRanges(rangesRes.data);
      setForms(formsRes.data);
      setPackagings(packagingsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      console.error('Failed to fetch config', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePricingClick = (brandName: string) => {
    setSelectedBrand(brandName);
    setShowPricingModal(true);
  };

  const handlePricingModalClose = () => {
    setShowPricingModal(false);
    setSelectedBrand('');
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950 overflow-y-auto md:overflow-hidden">
      <ModuleHeader
        icon={Sliders}
        title="Configurateur"
        subtitle="Gérez les marques, gammes, formes galéniques et conditionnements"
      />

      <div className="flex-1 p-3 sm:p-4 md:overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:h-full md:[grid-template-rows:repeat(3,minmax(0,1fr))]">
          <ConfigSection
            title="Marques"
            type="brands"
            items={brands}
            loading={loading}
            onRefresh={fetchData}
            onPricingClick={handlePricingClick}
          />
          <ConfigSection
            title="Gammes"
            type="ranges"
            items={ranges}
            loading={loading}
            onRefresh={fetchData}
          />
          <ConfigSection
            title="Unités"
            type="units"
            items={units}
            loading={loading}
            onRefresh={fetchData}
          />
          <ConfigSection
            title="Formes Galéniques"
            type="forms"
            items={forms}
            loading={loading}
            onRefresh={fetchData}
          />
          <ConfigSection
            title="Conditionnements"
            type="packagings"
            items={packagings}
            loading={loading}
            onRefresh={fetchData}
          />
          <DocCategorySection
            onRefresh={fetchData}
          />
          <ClientNotificationRecipientsSection
            onRefresh={fetchData}
          />
          <ContactRolesSection
            onRefresh={fetchData}
          />
          <PaymentTermsSection />
        </div>
      </div>

      {/* Brand Pricing Modal */}
      <BrandPricingModal
        isOpen={showPricingModal}
        onClose={handlePricingModalClose}
        brandName={selectedBrand}
      />
    </div>
  );
}